import { inject, NgModule } from "@angular/core";
import { Router, RouterModule, Routes, UrlSerializer } from "@angular/router";

import { AuthGuard } from "@vault/angular/guards/auth.guard";
import { InitialRedirectGuard } from "@vault/angular/guards/initialRoute.guard";
import { LockGuard } from "@vault/angular/guards/lock.guard";
import { UnauthGuard } from "@vault/angular/guards/unauth.guard";
import { EstimatesComponent } from "@vault/web-vault/app/gloss/estimates/estimates.component";
import { ManageAccountsComponent } from "@vault/web-vault/app/gloss/manage/manage-accounts/manage-accounts.component";
import { CabinetComponent } from "@vault/web-vault/app/gloss/cabinet/cabinet.component";
import { ManageSymbolsComponent } from "@vault/web-vault/app/gloss/manage/manage-symbols/manage-symbols.component";
import { ManageCategoriesComponent } from "@vault/web-vault/app/gloss/manage/manage-categories/manage-categories.component";
import { ManageClassificationsComponent } from "@vault/web-vault/app/gloss/manage/manage-classifications/manage-classifications.component";
import { ManageInstitutionsComponent } from "@vault/web-vault/app/gloss/manage/manage-institutions/manage-institutions.component";
import { OrganizationPageComponent } from "@vault/web-vault/app/gloss/manage/manage-organizations/organization-page.component";
import { ManagePreferencesComponent } from "@vault/web-vault/app/gloss/manage/manage-preferences/manage-preferences.component";
import { ViewsComponent } from "@vault/web-vault/app/gloss/views/views.component";
import {
  GlossRouteEnum,
  GlossRoutes,
  MainSettingsRoute,
} from "@vault/web-vault/app/models/enum/routes.enum";
import { AdvancedFeaturesComponent } from "@vault/web-vault/app/gloss/settings/advanced-features/advanced-features.component";
import { ManageSecurityComponent } from "@vault/web-vault/app/gloss/settings/manage-security/manage-security.component";
import { ProfileComponent } from "@vault/web-vault/app/gloss/settings/profile.component";
import { SettingsRouteComponent } from "@vault/web-vault/app/gloss/settings/settings-route.component";
import { SettingsComponent } from "@vault/web-vault/app/gloss/settings/settings.component";
import { UserAccessPageComponent } from "@vault/web-vault/app/gloss/settings/access/user-access/user-access-page.component";

import { DeviceApprovalRequiredComponent } from "@vault/web-vault/app/gloss/others/device-approval-required.component";
import { FirstDeviceSetupComponent } from "@vault/web-vault/app/gloss/others/first-device-setup.component";
import { FirstDeviceSetupService } from "@vault/web-vault/app/services/DataService/device-trust/first-device-setup.service";
import { HintComponent } from "@vault/web-vault/app/gloss/hint/hint.component";
import { LockComponent } from "@vault/web-vault/app/gloss/lock/lock.component";
import { LoginWithDeviceComponent } from "@vault/web-vault/app/gloss/login/login-with-device.component";
import { LoginComponent } from "@vault/web-vault/app/gloss/login/login.component";
import { RecoverDeleteComponent } from "@vault/web-vault/app/gloss/others/recover-delete.component";
import { RecoverTwoFactorComponent } from "@vault/web-vault/app/gloss/others/recover-two-factor.component";
import { RegisterFormComponent } from "@vault/web-vault/app/gloss/register-form/register-form.component";
import { RemovePasswordComponent } from "@vault/web-vault/app/gloss/others/remove-password.component";
import { SetPasswordComponent } from "@vault/web-vault/app/gloss/others/set-password.component";
import { TwoFactorComponent } from "@vault/web-vault/app/gloss/others/two-factor.component";
import { UpdatePasswordComponent } from "@vault/web-vault/app/gloss/others/update-password.component";
import { VerifyEmailTokenComponent } from "@vault/web-vault/app/gloss/others//verify-email-token.component";
import { AcceptEmergencyComponent } from "@vault/web-vault/app/gloss/others/accept-emergency.component";
import { AcceptTeamComponent } from "@vault/web-vault/app/gloss/others/accept-team.component";
import { HomeGuard } from "./guards/home.guard";
import { FrontendLayoutComponent } from "./layouts/frontend-layout.component";
import { UserLayoutComponent } from "./layouts/user-layout.component";
import { GeneratorComponent } from "./tools/generator.component";
import { ToolsComponent } from "./tools/tools.component";
import { PlansComponent } from "@vault/web-vault/app/gloss/settings/access/plans/plans.component";
import { SubscriptionPageComponent } from "@vault/web-vault/app/gloss/settings/access/subscription/subscription.component";
import { StorageComponent } from "@vault/web-vault/app/gloss/settings/storage/storage.component";
import { ReferenceDataImportComponent } from "@vault/web-vault/app/components/reference-data-import/reference-data-import.component";
import { EarnComponent } from "@vault/web-vault/app/gloss/earn/earn.component";
import { BudgetComponent } from "./gloss/budget/budget.component";
import { CreditsComponent } from "./gloss/credits/credits.component";
import { PluginsComponent } from "./gloss/plugins/plugins.component";
import { GlossGroceryComponent } from "./gloss/gloss-grocery/gloss-grocery.component";
import { RecurringDetectorComponent } from "./gloss/plugins/recurring-detector/recurring-detector.component";
import { ManageSourceExplorerComponent } from "@vault/web-vault/app/gloss/manage/manage-source-explorer/manage-source-explorer.component";
import { OrganizationsGuard } from "./guards/organizations.guard";
import { BetaAccessGuard } from "./guards/beta-access.guard";
import { SourceExplorerGuard } from "./guards/source-explorer.guard";
import { HelpGuideComponent } from "./gloss/help-guide/help-guide.component";
import { TwoFactorSetupComponent } from "@bitwarden/web-vault/app/gloss/settings/two-factor-setup.component";
import { NotificationsComponent } from "@vault/web-vault/app/gloss/settings/notifications/notifications.component";
import { TrailingSlashUrlSerializer } from "./core/trailing-slash-url-serializer";

export const routes: Routes = [
  {
    // Plaid Link OAuth redirect landing. Reached as `#/plaid-oauth?oauth_state_id=...`
    // (the app uses hash routing); the static `src/plaid-oauth.html` serves the
    // bare `/plaid-oauth` path and forwards into this route to resume Link.
    path: "plaid-oauth",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./services/syncing/plaid/plaid-oauth-landing/plaid-oauth-landing.component").then(
        (m) => m.PlaidOauthLandingComponent,
      ),
  },
  {
    path: "",
    component: FrontendLayoutComponent,
    data: { doNotSaveUrl: true },
    children: [
      {
        path: "",
        pathMatch: "full",
        children: [], // Children lets us have an empty component.
        canActivate: [HomeGuard], // Redirects either to views, login or lock page.
      },
      {
        path: "login",
        component: LoginComponent,
        canActivate: [UnauthGuard],
      },
      {
        path: "register",
        component: RegisterFormComponent,
      },
      {
        path: "hint",
        component: HintComponent,
        canActivate: [UnauthGuard],
        data: { text: "passwordHint" },
      },
      {
        path: "lock",
        component: LockComponent,
        canActivate: [LockGuard],
      }, // todo we might be able to remove these routes as they're not used
      { path: "2fa", component: TwoFactorComponent, canActivate: [UnauthGuard] },
      {
        path: "trial",
        redirectTo: "register",
        pathMatch: "full",
      },
      {
        path: "set-password",
        component: SetPasswordComponent,
        data: { titleId: "setMasterPassword" },
      },
      {
        path: "first-device-setup",
        component: FirstDeviceSetupComponent,
        // Only reachable right after an isFirstDevice login (see FirstDeviceSetupService.armSetup).
        canActivate: [
          () =>
            inject(FirstDeviceSetupService).isSetupArmed() ||
            inject(Router).createUrlTree([GlossRoutes.Views]),
        ],
        data: { titleId: "firstDeviceSetupTitle" },
      },
      {
        path: "device-approval-required",
        component: DeviceApprovalRequiredComponent,
        data: { titleId: "deviceApprovalRequiredTitle" },
      },
      {
        path: "login-with-device",
        component: LoginWithDeviceComponent,
        data: { titleId: "loginWithDevice" },
      },
      {
        path: "two-factor-setup",
        component: TwoFactorSetupComponent,
        canActivate: [AuthGuard],
        data: { doNotSaveUrl: true },
      },
      { path: "verify-email", component: VerifyEmailTokenComponent },
      {
        path: "accept-team",
        component: AcceptTeamComponent,
        data: { doNotSaveUrl: true },
      },
      // Invite emails sent before the page was renamed still link here; query params carry over.
      { path: "accept-organization", pathMatch: "full", redirectTo: "accept-team" },
      {
        path: "accept-emergency",
        component: AcceptEmergencyComponent,
        data: { doNotSaveUrl: true },
      },
      { path: "recover", pathMatch: "full", redirectTo: "recover-2fa" },
      {
        path: "recover-2fa",
        component: RecoverTwoFactorComponent,
        canActivate: [UnauthGuard],
        data: { titleId: "recoverAccountTwoStep" },
      },
      {
        path: "recover-delete",
        component: RecoverDeleteComponent,
        canActivate: [UnauthGuard],
        data: { titleId: "deleteAccount" },
      },
      {
        path: "update-password",
        component: UpdatePasswordComponent,
        canActivate: [AuthGuard],
        data: { titleId: "updatePassword" },
      },
      {
        path: "remove-password",
        component: RemovePasswordComponent,
        canActivate: [AuthGuard],
        data: { titleId: "removeMasterPassword" },
      },
    ],
  },
  {
    path: "",
    component: UserLayoutComponent,
    canActivate: [AuthGuard, InitialRedirectGuard],
    children: [
      // The old vault page is gone and nothing in the app navigates to /vault any more; kept so
      // old bookmarks and mobile deep links still land somewhere.
      { path: "vault", redirectTo: GlossRoutes.Views, pathMatch: "full" },
      // The trial-plan page is gone. Phone app builds that predate it, and Stripe checkout
      // sessions created before it was removed, still send people back to /membership-plan; with
      // no match (and no "**" route) that is a blank page. The query string, e.g. ?plan=year,
      // goes along.
      {
        path: "membership-plan",
        redirectTo: `${GlossRoutes.Settings}/${MainSettingsRoute.Plans}`,
        pathMatch: "full",
      },
      { path: GlossRoutes.Estimates, component: EstimatesComponent },
      {
        path: GlossRoutes.Views,
        component: ViewsComponent,
      },
      {
        path: GlossRoutes.Budget,
        component: BudgetComponent,
      },
      { path: GlossRoutes.Cabinet, component: CabinetComponent },
      { path: GlossRoutes.Earn, component: EarnComponent },
      { path: GlossRoutes.Credits, component: CreditsComponent },
      { path: GlossRoutes.Plugins, component: PluginsComponent },
      { path: GlossRoutes.GlossGrocery, component: GlossGroceryComponent },
      {
        path: GlossRoutes.RecurringDetector,
        component: RecurringDetectorComponent,
        canActivate: [BetaAccessGuard],
      },
      { path: GlossRoutes.Symbol, component: ManageSymbolsComponent },
      { path: GlossRoutes.Guide, component: HelpGuideComponent },
      { path: GlossRoutes.Import, component: ReferenceDataImportComponent },
      { path: GlossRoutes.Reference, component: ReferenceDataImportComponent },
      {
        path: "manage",
        children: [
          { path: "", pathMatch: "full", redirectTo: GlossRouteEnum.account },
          {
            path: GlossRoutes.Account,
            component: ManageAccountsComponent,
          },
          {
            path: GlossRoutes.Classifications,
            component: ManageClassificationsComponent,
          },
          {
            path: GlossRoutes.Categories,
            component: ManageCategoriesComponent,
          },
          {
            path: GlossRoutes.Institutions,
            component: ManageInstitutionsComponent,
          },
          {
            path: GlossRoutes.Collections,
            component: OrganizationPageComponent,
            canActivate: [OrganizationsGuard],
          },
          {
            path: GlossRoutes.Organizations,
            pathMatch: "full",
            redirectTo: GlossRoutes.Collections,
          },
          {
            path: GlossRoutes.Preferences,
            component: ManagePreferencesComponent,
          },
          {
            path: GlossRoutes.SourceExplorer,
            component: ManageSourceExplorerComponent,
            canActivate: [SourceExplorerGuard],
          },
        ],
      },
      {
        path: GlossRoutes.Settings,
        component: SettingsComponent,
      },
      {
        path: GlossRoutes.Settings,
        component: SettingsRouteComponent,
        children: [
          { path: "", pathMatch: "full", redirectTo: GlossRoutes.Settings },
          {
            path: MainSettingsRoute.Profile,
            component: ProfileComponent,
            data: { titleId: "profile" },
          },
          {
            path: MainSettingsRoute.Security,
            component: ManageSecurityComponent,
            data: { titleId: "security" },
          },
          {
            path: MainSettingsRoute.Storage,
            component: StorageComponent,
            data: { titleId: "storage" },
          },
          {
            path: MainSettingsRoute.AdvancedFeatures,
            component: AdvancedFeaturesComponent,
            data: { titleId: "advancedFeatures" },
          },
          {
            path: MainSettingsRoute.Preferences,
            component: ManagePreferencesComponent,
            data: { titleId: "preferences" },
          },
          {
            path: MainSettingsRoute.Access,
            component: UserAccessPageComponent,
            data: { titleId: "access" },
          },
          {
            path: MainSettingsRoute.Plans,
            component: PlansComponent,
            data: { titleId: "bundles" },
          },
          // Legacy URL: keep old /settings/plans links working after the rename to /settings/bundles
          { path: "plans", redirectTo: MainSettingsRoute.Plans, pathMatch: "full" },
          {
            path: MainSettingsRoute.Subscription,
            component: SubscriptionPageComponent,
            data: { titleId: "billing" },
          },
          {
            path: MainSettingsRoute.Notifications,
            component: NotificationsComponent,
            data: { titleId: "notifications" },
          },
        ],
      },
      {
        path: `${GlossRoutes.Settings}/` + MainSettingsRoute.Security,
        loadChildren: () =>
          import(
            "@vault/web-vault/app/gloss/settings/manage-security/security-routing.module"
          ).then((m) => m.SecurityRoutingModule),
      },
      {
        path: "tools",
        component: ToolsComponent,
        canActivate: [AuthGuard],
        children: [
          { path: "", pathMatch: "full", redirectTo: "generator" },
          {
            path: "",
            loadChildren: () =>
              import("./tools/import-export/import-export.module").then(
                (m) => m.ImportExportModule,
              ),
          },
          {
            path: "generator",
            component: GeneratorComponent,
            data: { titleId: "generator" },
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: true,
      paramsInheritanceStrategy: "always",
      // enableTracing: true,
    }),
  ],
  // Trailing-slash hash URLs (e.g. "/#/verify-email/?userId=...&token=...") would otherwise
  // fail to match their route and lose the token — see TrailingSlashUrlSerializer.
  providers: [{ provide: UrlSerializer, useClass: TrailingSlashUrlSerializer }],
  exports: [RouterModule],
})
export class OssRoutingModule {}
